import React, { useEffect, useMemo } from "react"
// eslint-disable-next-line no-restricted-imports
import { FlexContainer } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import { useTelemedicineAccess } from "@internal/telemedicine"
import { useNavAvailability } from "#/services/feature-availability/useNavAvailability"
import { ProviderDetails } from "./ProviderDetails"
import { isCarrotClinicProvider, Provider } from "../../types/Providers"
import { PaginationBar } from "./PaginationBar"
import { useHistory } from "react-router-dom"
import { getHeap } from "../../utils/heap"
import { ProviderFinderParam, providerFinderRootPath } from "../../utils/providerFinderSearchUrl"
import { HeapPropertyValue } from "#/utils/heap"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { FormattedMessage } from "react-intl"
import { Alert, Box, Divider, Progress, Stack, Tile, Typography, useTheme } from "@carrotfertility/carotene-core"
import { Loading } from "../Loading"
import useGetCustomNetworks from "#/components/views/app/navigation/hooks/useGetCustomNetworks"
import { TelemedicineForMenopausePfBanner } from "#/components/telemedicine/TelemedicineForMenopausePfBanner"
import { CarrotMatchDialog } from "#/components/carrot-match-fertility-clinic-precision-matching/CarrotMatchDialog"
import { useCarrotMatchEnabled } from "#/components/carrot-match-fertility-clinic-precision-matching/hooks/useShowCarrotMatch"
import { CarrotMatchProviderFinderBanner } from "#/components/carrot-match-fertility-clinic-precision-matching/CarrotMatchProviderFinderBanner"
import { CarrotClinicCard } from "#/pages/provider-finder/components/results/CarrotClinicCard"
import { CheckYourBenefitAlert } from "#/pages/provider-finder/components/results/CheckYourBenefitAlert"
import { useProviderTypeSearched } from "#/pages/provider-finder/components/results/useProviderTypeSearched"
import { useEnhancedEmptyState } from "#/pages/provider-finder/hooks/useEnhancedEmptyState"
import {
  EnhancedEmptyStateProps,
  ProviderEligibilityMessage
} from "#/pages/provider-finder/components/results/ProviderEligibilityMessage"
import { ProviderType } from "#/pages/provider-finder/utils/parentIdMapping"
import { useGetProviderFinderNetworkCustomization } from "#/components/views/app/navigation/hooks/useGetProviderFinderNetworkCustomization"
import { useProviderFinderViewedEvent } from "#/pages/provider-finder/hooks/useProviderFinderViewedEvent"
import { useCustomNetworkCustomer } from "#/pages/provider-finder/utils/useCustomNetworkCustomer"
import { useMemberProgramFlags } from "#/services/user/hooks/useMemberProgram"

const ITEMS_PER_PAGE = 25
const DOULA_ITEMS_PER_PAGE = 15

type ResultListProps = {
  providers: Provider[]
  benefit: Benefit
}

export function ResultList({ providers, benefit }: ResultListProps): JSX.Element {
  const history = useHistory()
  const params = useMemo(() => new URLSearchParams(history.location.search), [history.location.search])
  // Normalize missing/invalid page to 1 so a deep link without ?page= doesn't
  // produce a negative startIndex.
  const requestedPage = Number(params.get(ProviderFinderParam.Page))
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1
  const searchLocation = params.get(ProviderFinderParam.Search)
  const stateCode = params.get(ProviderFinderParam.State)
  const carrotPartnersOnly = params.get(ProviderFinderParam.CarrotPartner) ? "true" : "false"

  // Skip the Carrot Clinic pin when the booking flow would just redirect the member home.
  const { data: telemedicineAccess } = useTelemedicineAccess()
  const { data: programFlags } = useMemberProgramFlags()
  const { show } = useNavAvailability()
  const canBookTelemedicine =
    show.appointments &&
    telemedicineAccess != null &&
    telemedicineAccess.eligible &&
    telemedicineAccess.entry.status !== "single_unqualified"
  // Always strip the sentinel from the list even when the pin is hidden — otherwise the -1 row
  // falls back into the regular list as a broken clinic card.
  const carrotClinic = canBookTelemedicine ? providers.find(isCarrotClinicProvider) : undefined
  const listProviders = providers.filter((p) => !isCarrotClinicProvider(p))
  // Only pin the Carrot Clinic entry on page 1 of the paginated results.
  // Wait until we have a known program flag before showing the carrot clinic card.
  const carrotClinicVisible = programFlags != null && carrotClinic != null && page === 1

  const { providerTypeSearched, isDoulasSearched, isFertilityClinicsSearched } = useProviderTypeSearched()
  // Header count reflects what the member actually sees; Heap tracks the raw
  // response so pre-launch dashboards stay continuous.
  const shownProviderCount = listProviders.length + (carrotClinicVisible ? 1 : 0)
  const rawProviderCount = providers.length
  const itemsPerPage = isDoulasSearched ? DOULA_ITEMS_PER_PAGE : ITEMS_PER_PAGE
  const pageCount = Math.ceil(listProviders.length / itemsPerPage)

  const startIndex = (page - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage - 1
  const currentItems = listProviders.slice(startIndex, endIndex + 1)
  const carrotPartnerCount = currentItems.filter((p) => p.carrotPartner).length

  const customNetworks = useGetCustomNetworks()
  const { isRtxSegment } = useCustomNetworkCustomer()
  const isCustomNetworks =
    (providerTypeSearched === ProviderType.FertilityClinics && customNetworks.data?.isInFertilityNetwork) ||
    (providerTypeSearched === ProviderType.Menopause && customNetworks.data?.isInMenopauseNetwork) ||
    (providerTypeSearched === ProviderType.LowTestosterone && customNetworks.data?.isInLowTNetwork)
  const enhancedEmptyState = useEnhancedEmptyState()
  const shouldDisplayProviderEligibilityBannerAtTop =
    enhancedEmptyState.isEnhancedEmptyState && listProviders.every((provider) => provider.isVirtual)
  const shouldDisplayProviderEligibilityBannerAtBottom =
    enhancedEmptyState.isEnhancedEmptyState && listProviders.some((provider) => !provider.isVirtual)

  useProviderFinderViewedEvent(params, searchLocation, providerTypeSearched, rawProviderCount, "list", page)

  useEffect(
    function trackProviderSearchResultsRendered() {
      const customProperties: Record<string, HeapPropertyValue> = {
        "Provider Count": rawProviderCount,
        "Carrot Partners Filter on (true/false)": carrotPartnersOnly,
        "Total Page Count": pageCount,
        "Results per Page": itemsPerPage,
        "Page Number": page
      }
      if (carrotPartnersOnly === "false") {
        customProperties["Carrot Partner Count"] = carrotPartnerCount
      }
      getHeap().track("Provider Search Results Rendered", customProperties)
    },
    [rawProviderCount, carrotPartnersOnly, pageCount, carrotPartnerCount, page, itemsPerPage]
  )

  function handlePageChange(event: React.ChangeEvent, page: number): void {
    const params = new URLSearchParams(history.location.search)
    params.set(ProviderFinderParam.Page, page.toString())
    history.push(`${providerFinderRootPath}/providers?${params}`)
  }

  const shouldDisplayInNetworkStatusBanner = useShouldDisplayInNetworkStatusBanner()

  const { isCarrotMatchEnabled: shouldDisplayCarrotMatchBanner } = useCarrotMatchEnabled()

  if (customNetworks.isLoading || enhancedEmptyState.isLoadingContent) {
    return <Loading />
  }
  return (
    <>
      <Box display="flex" flexDirection="column" padding="0 0 96px 0">
        <Stack gap={(theme) => theme.tokens.spacing.lg} marginBottom={(theme) => theme.tokens.spacing.xl}>
          <TelemedicineForMenopausePfBanner />
          {isCustomNetworks && !isRtxSegment && <CustomNetworkAlert />}
          <CheckYourBenefitAlert isCustomNetworks={isCustomNetworks} />
          {shouldDisplayProviderEligibilityBannerAtTop && (
            <ProviderEligibilityBanner
              displayName={enhancedEmptyState.emptyStateProviderTypeDisplayName}
              dialogContent={enhancedEmptyState.emptyStateContent}
            />
          )}
          {shouldDisplayInNetworkStatusBanner && <InNetworkStatusAlert />}
          {shouldDisplayCarrotMatchBanner && isFertilityClinicsSearched && <CarrotMatchProviderFinderBanner />}
        </Stack>

        <ResultCount providerCount={shownProviderCount} />
        {(carrotClinicVisible || listProviders.length > 0) && (
          <Results
            providers={currentItems}
            benefit={benefit}
            searchLocation={searchLocation}
            startIndex={startIndex}
            leadingItem={
              carrotClinicVisible ? (
                <CarrotClinicCard provider={carrotClinic} stateCode={stateCode} isConsumer={programFlags.isConsumer} />
              ) : undefined
            }
          />
        )}
        {listProviders.length > 0 && (
          <>
            <ResultsPageStatus startIndex={startIndex} endIndex={endIndex} providerCount={listProviders.length} />
            <PaginationBar count={pageCount} onChange={handlePageChange} page={page} />
          </>
        )}
        {shouldDisplayProviderEligibilityBannerAtBottom && (
          <Box marginTop={(theme) => theme.tokens.spacing.lg}>
            <ProviderEligibilityBanner
              displayName={enhancedEmptyState.emptyStateProviderTypeDisplayName}
              dialogContent={enhancedEmptyState.emptyStateContent}
            />
          </Box>
        )}
      </Box>
      <CarrotMatchDialog />
    </>
  )
}

const ProviderEligibilityBanner = ({ displayName, dialogContent }: EnhancedEmptyStateProps): JSX.Element => {
  const theme = useTheme()
  return (
    <Tile color="info">
      <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>
        <Typography variant="h5">
          <FormattedMessage defaultMessage="Can't find what you're looking for?" />
        </Typography>
        <ProviderEligibilityMessage displayName={displayName} dialogContent={dialogContent} useMaxWidth={true} />
      </Stack>
    </Tile>
  )
}

const CustomNetworkAlert = () => (
  <Alert>
    <FormattedMessage defaultMessage="These are Carrot providers that are in-network with your health plan. Always check with your health plan that the services you are looking for are covered." />
  </Alert>
)

const useShouldDisplayInNetworkStatusBanner = () => {
  const { data: providerFinderNetworkCustomization } = useGetProviderFinderNetworkCustomization()
  return providerFinderNetworkCustomization?.displayInNetworkStatusBanner
}

const InNetworkStatusAlert = () => (
  <Alert severity="info">
    <FormattedMessage defaultMessage="Because a provider's in-network status can change, always verify with your plan before receiving care." />
  </Alert>
)

const ResultCount = ({ providerCount }: { providerCount: number }) => (
  <Box display="flex" justifyContent="flex-end" marginBottom={(theme) => theme.tokens.spacing.md}>
    <Typography variant="body2" color={(theme) => theme.palette.text.secondary}>
      <FormattedMessage
        defaultMessage="{providerCount} results"
        values={{
          providerCount: providerCount
        }}
      />
    </Typography>
  </Box>
)

type ResultsProps = {
  providers: Provider[]
  benefit: Benefit
  searchLocation: string
  startIndex: number
  leadingItem?: React.ReactNode
}

const Results = ({ providers, benefit, searchLocation, startIndex, leadingItem }: ResultsProps) => {
  const theme = useTheme()

  const results = providers?.map((provider, index) => (
    <Result
      key={provider.providerUuid}
      provider={provider}
      rank={startIndex + index + 1}
      searchLocation={searchLocation}
      benefit={benefit}
    />
  ))

  const hasResults = results?.length > 0

  return (
    <Stack
      sx={{ "> li::before": { display: "none" } }}
      component="ol"
      direction="column"
      divider={<Divider />}
      gap={theme.spacing(theme.tokens.spacing.lg)}
    >
      {leadingItem && <Box component="li">{leadingItem}</Box>}
      {hasResults ? results : leadingItem ? null : <ResultsLoadingIndicator />}
    </Stack>
  )
}

type ResultProps = {
  provider: Provider
  rank: number
  searchLocation: string
  benefit: Benefit
}
const Result = ({ provider, rank, searchLocation, benefit }: ResultProps) => {
  return (
    <Box component="li">
      <ProviderDetails provider={provider} rank={rank} searchLocation={searchLocation} benefit={benefit} />
    </Box>
  )
}

const ResultsLoadingIndicator = () => (
  <FlexContainer height="180px" justifyContent="center" alignItems="center">
    <Box display="flex" justifyContent="center" alignItems="center">
      <Progress />
    </Box>
  </FlexContainer>
)

type ResultsPageStatusProps = {
  startIndex: number
  endIndex: number
  providerCount: number
}

const ResultsPageStatus = ({ startIndex, endIndex, providerCount }: ResultsPageStatusProps) => {
  const firstPageResultRank = startIndex + 1
  const lastProviderRank = Math.min(endIndex + 1, providerCount)

  return (
    <Box
      display="flex"
      justifyContent="center"
      width="100%"
      paddingTop={(theme) => theme.spacing(theme.tokens.spacing.xl)}
      paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.xl)}
    >
      <Typography variant="body2" color={(theme) => theme.palette.text.tertiary}>
        <FormattedMessage
          defaultMessage="{firstPageResultRank}-{lastProviderRank} of {providerCount}"
          values={{
            firstPageResultRank: firstPageResultRank,
            lastProviderRank: lastProviderRank,
            providerCount: providerCount
          }}
        />
      </Typography>
    </Box>
  )
}
